import { getConsentSnapshot, subscribeConsent } from './consent';

export interface Touch {
  occurred_at: string;
  landing_path: string;
  referrer_host?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  campaign_id?: string;
  ad_id?: string;
  gclid?: string;
  gbraid?: string;
  wbraid?: string;
}
interface Acquisition { first: Touch; last: Touch; expires: number }
const key = 'bb_acquisition_v1';
const fields = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  'campaign_id', 'ad_id', 'gclid', 'gbraid', 'wbraid'] as const;

export function landingTouch(url: URL, referrer: string, now = new Date()): Touch {
  const touch: Touch = { occurred_at: now.toISOString(), landing_path: url.pathname.slice(0, 256) };
  for (const field of fields) {
    const value = url.searchParams.get(field);
    if (value && value.length <= (['gclid', 'gbraid', 'wbraid'].includes(field) ? 512 : 128)) {
      if ((field === 'campaign_id' || field === 'ad_id') && !/^\d{1,32}$/.test(value)) continue;
      touch[field] = value;
    }
  }
  if (referrer) {
    try {
      const host = new URL(referrer).hostname;
      if (host !== url.hostname && !['accounts.google.com', 'accounts.youtube.com'].includes(host)) {
        touch.referrer_host = host;
      }
    } catch { /* Invalid referrer does not establish a source. */ }
  }
  return touch;
}

export function readAcquisition(): Acquisition | null {
  if (getConsentSnapshot().advertising !== true) return null;
  try {
    const value = JSON.parse(localStorage.getItem(key) || 'null') as Acquisition | null;
    if (!value || value.expires <= Date.now() || !value.first?.occurred_at || !value.last?.occurred_at) {
      localStorage.removeItem(key);
      return null;
    }
    return value;
  } catch { return null; }
}

// Signup metadata is consumed by the account-creation transaction. Click IDs
// remain browser-only; the database needs only their source-classification signal.
export function signupAcquisitionSnapshot() {
  const consent = getConsentSnapshot();
  const acquisition = consent.decided && consent.advertising === true ? readAcquisition() : null;
  const signupTouch = (touch: Touch | undefined): (Omit<Touch, 'gclid' | 'gbraid' | 'wbraid'>
    & { google_click_id_present: boolean }) | null => {
    if (!touch) return null;
    const allowed = ['referrer_host', 'utm_source',
      'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'campaign_id', 'ad_id'] as const;
    return { occurred_at: touch.occurred_at, landing_path: touch.landing_path,
      ...Object.fromEntries(allowed.filter(field => touch[field] !== undefined)
      .map(field => [field, touch[field]])),
      google_click_id_present: Boolean(touch.gclid || touch.gbraid || touch.wbraid) };
  };
  return { version: 1, ...consent,
    first: signupTouch(acquisition?.first), last: signupTouch(acquisition?.last) };
}

export function startAcquisitionCapture() {
  // Hold the original landing only in memory while the CMP loads; no pre-consent storage.
  const landing = landingTouch(new URL(window.location.href), document.referrer);
  const capture = () => {
    if (!getConsentSnapshot().decided) return;
    if (getConsentSnapshot().advertising !== true) {
      try { localStorage.removeItem(key); } catch { /* Nothing can be persisted. */ }
      return;
    }
    const prior = readAcquisition();
    // An OAuth/confirmation return without an original touch is missing evidence.
    if (!prior && landing.landing_path.startsWith('/auth/')) return;
    const attributable = fields.some(field => Boolean(landing[field])) || Boolean(landing.referrer_host);
    const value = { first: prior?.first || landing,
      last: prior && !attributable ? prior.last : landing,
      expires: prior?.expires || Date.parse(landing.occurred_at) + 30 * 86400000 };
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Report missing attribution. */ }
  };
  capture();
  return subscribeConsent(capture);
}
